import React, { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Collapse,
  Drawer,
  Empty,
  Grid,
  Input,
  Tabs,
  Tag,
  Typography,
  theme,
} from 'antd';
import { ArrowLeftOutlined, PlayCircleOutlined, SearchOutlined } from '@ant-design/icons';
import ReproductorTutorial from './ReproductorTutorial';
import { BASE_CONTENIDO } from './contenido';
import { buscarTutoriales, esNuevo, urlContenido } from './catalogo';
import { formatoTiempo } from './capitulos';
import NovedadItem from './NovedadItem';

const { Text, Paragraph } = Typography;

function Tarjeta({ tutorial, base, nuevo, onElegir }) {
  const { token } = theme.useToken();
  const poster = urlContenido(base, tutorial.poster);
  return (
    <button
      type="button"
      data-testid="tarjeta-tutorial"
      onClick={() => onElegir(tutorial.id)}
      style={{
        display: 'flex',
        gap: 12,
        width: '100%',
        textAlign: 'left',
        padding: 8,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadiusLG,
        background: token.colorBgContainer,
        color: token.colorText,
        cursor: 'pointer',
        font: 'inherit',
      }}
    >
      <div
        style={{
          width: 120,
          height: 68,
          flex: 'none',
          borderRadius: token.borderRadius,
          background: poster ? `center / cover url(${poster})` : token.colorFillSecondary,
          display: 'grid',
          placeItems: 'center',
          color: '#fff',
        }}
      >
        <PlayCircleOutlined style={{ fontSize: 26, textShadow: '0 1px 4px rgba(0,0,0,.6)' }} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 500 }}>
          {tutorial.titulo} {nuevo ? <Tag color="green">Nuevo</Tag> : null}
        </div>
        {tutorial.resumen ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {tutorial.resumen}
          </Text>
        ) : null}
        {tutorial.duracionS ? (
          <div style={{ fontSize: 12, color: token.colorTextTertiary }}>
            {formatoTiempo(tutorial.duracionS)}
          </div>
        ) : null}
      </div>
    </button>
  );
}

// Centro de ayuda: tutoriales agrupados por área, con buscador. Al elegir uno se muestra su reproductor.
//
// Props:
//   open, onClose — estado del Drawer.
//   catalogo — catálogo ya normalizado ({ grupos, tutoriales }) o null mientras carga.
//   tutorialId — tutorial a mostrar al abrir (p. ej. el de la pantalla actual); null = la lista.
//   vista — pestaña inicial: 'tutoriales' (por defecto) o 'novedades' (el historial).
//   novedades — estado compartido de las novedades ({ vigentes, vistas, sinLeer, marcarLeidas }).
//   hoy — 'YYYY-MM-DD' para la marca "Nuevo"; inyectable para pruebas.
export default function CentroDeAyuda({
  open,
  onClose,
  catalogo,
  tutorialId = null,
  vista = 'tutoriales',
  novedades = null,
  base = BASE_CONTENIDO,
  hoy,
}) {
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const [texto, setTexto] = useState('');
  const [elegido, setElegido] = useState(null);
  const idActivo = elegido ?? tutorialId;
  const tutorial = catalogo?.tutoriales.find((t) => t.id === idActivo) ?? null;
  const fechaHoy = hoy ?? new Date().toISOString().slice(0, 10);

  const grupos = useMemo(
    () => (catalogo ? buscarTutoriales(catalogo, texto) : []),
    [catalogo, texto]
  );

  const cerrar = () => {
    setElegido(null);
    setTexto('');
    onClose?.();
  };

  const listaTutoriales = (
    <>
      <Input
        allowClear
        prefix={<SearchOutlined />}
        placeholder="Buscar un tutorial"
        aria-label="Buscar un tutorial"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        style={{ marginBottom: token.marginMD }}
      />
      {!catalogo ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Cargando tutoriales…" />
      ) : grupos.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            texto ? 'Ningún tutorial coincide con tu búsqueda.' : 'Aún no hay tutoriales.'
          }
        />
      ) : (
        <Collapse
          defaultActiveKey={grupos.map((g) => g.id)}
          key={texto}
          ghost
          items={grupos.map((g) => ({
            key: g.id,
            label: (
              <Text strong>
                {g.titulo} <Text type="secondary">({g.tutoriales.length})</Text>
              </Text>
            ),
            children: (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {g.tutoriales.map((t) => (
                  <Tarjeta
                    key={t.id}
                    tutorial={t}
                    base={base}
                    nuevo={esNuevo(t, fechaHoy)}
                    onElegir={setElegido}
                  />
                ))}
              </div>
            ),
          }))}
        />
      )}
    </>
  );

  const vistasSet = new Set(novedades?.vistas ?? []);
  const verTutorialDe = (idTutorial, idNovedad) => {
    novedades?.marcarLeidas([idNovedad]);
    setElegido(idTutorial);
  };
  const historial =
    !novedades || novedades.vigentes.length === 0 ? (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aún no hay novedades." />
    ) : (
      <>
        {novedades.sinLeer > 0 ? (
          <Button
            size="small"
            style={{ marginBottom: token.marginSM }}
            onClick={() => novedades.marcarLeidas(novedades.vigentes.map((p) => p.id))}
          >
            Marcar todas como leídas
          </Button>
        ) : null}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {novedades.vigentes.map((p) => (
            <NovedadItem
              key={p.id}
              publicacion={p}
              leida={vistasSet.has(p.id)}
              base={base}
              onVerTutorial={verTutorialDe}
            />
          ))}
        </div>
      </>
    );

  return (
    <Drawer
      open={open}
      onClose={cerrar}
      title={tutorial ? tutorial.titulo : 'Centro de ayuda'}
      width={screens.md ? 760 : '100%'}
      destroyOnHidden
    >
      {tutorial ? (
        <>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => setElegido('__lista__')}
            style={{ paddingLeft: 0, marginBottom: 8 }}
          >
            Todos los tutoriales
          </Button>
          <ReproductorTutorial tutorial={tutorial} base={base} />
          {tutorial.resumen ? (
            <Paragraph type="secondary" style={{ marginTop: token.marginSM }}>
              {tutorial.resumen}
            </Paragraph>
          ) : null}
        </>
      ) : (
        <Tabs
          key={vista}
          defaultActiveKey={vista}
          items={[
            { key: 'tutoriales', label: 'Tutoriales', children: listaTutoriales },
            {
              key: 'novedades',
              label: (
                <Badge count={novedades?.sinLeer ?? 0} size="small" offset={[10, -2]}>
                  Novedades
                </Badge>
              ),
              children: historial,
            },
          ]}
        />
      )}
    </Drawer>
  );
}
